import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ADMIN_NAV_GROUPS, ADMIN_NAV_ITEMS } from "./navItems";
import type { AdminNavGroupId, AdminNavItem } from "./navItems";
import { useConfig } from "../theme/ConfigContext";
import { useAdminFeatures } from "../features/AdminFeaturesContext";
import { ProviderSwitcher } from "../components/ProviderSwitcher";
import { useProvider } from "../providers/ProviderContext";

const EXPANDED_GROUPS_STORAGE_KEY = "equipped-admin-web-mantis:navExpandedGroups";

function readInitialExpandedGroups(
  activeGroupId?: AdminNavGroupId,
): Set<AdminNavGroupId> {
  const defaultIds = ADMIN_NAV_GROUPS.filter((group) => group.defaultExpanded).map(
    (group) => group.id,
  );
  let expanded = new Set<AdminNavGroupId>(defaultIds);

  try {
    const stored = window.localStorage.getItem(EXPANDED_GROUPS_STORAGE_KEY);
    if (stored != null) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        const knownIds = new Set<unknown>(ADMIN_NAV_GROUPS.map((group) => group.id));
        expanded = new Set(
          parsed.filter((id): id is AdminNavGroupId => knownIds.has(id)),
        );
      }
    }
  } catch {
    /* private-browsing / malformed storage — use descriptor defaults */
  }

  if (activeGroupId) expanded.add(activeGroupId);
  return expanded;
}

/**
 * The nav list inside the Drawer — adapted from Mantis Free's `layout/Dashboard/Drawer/
 * DrawerContent/Navigation`. The full drawer groups the feature-filtered destinations into
 * independently collapsible sections; the mini drawer keeps the same flat icon-only rail because
 * section labels do not fit there. Expansion state survives reloads, and navigation opens the
 * active destination's section.
 *
 * **The nav gating point** (docs/358-ADMIN-FEATURE-CONFIGURATION.md v0) — a disabled
 * feature's row is HIDDEN, not greyed (filtered out of the list entirely, not just
 * disabled), per the owner's explicit requirement.
 */
export function DrawerContent() {
  const location = useLocation();
  const { drawerOpen } = useConfig();
  const { has, hasCapability } = useAdminFeatures();
  const { deployment } = useProvider();
  const visibleItems = useMemo(
    () =>
      ADMIN_NAV_ITEMS.filter(
        (item) => has(item.feature) && (!item.requires || hasCapability(item.requires)),
      ),
    [has, hasCapability],
  );
  const visibleGroups = useMemo(
    () =>
      ADMIN_NAV_GROUPS.map((group) => ({
        ...group,
        items: visibleItems.filter((item) => item.group === group.id),
      })).filter((group) => group.items.length > 0),
    [visibleItems],
  );
  const activeGroupId = visibleItems.find(
    (item) =>
      location.pathname === item.route || location.pathname.startsWith(`${item.route}/`),
  )?.group;
  const [expandedGroupIds, setExpandedGroupIds] = useState(() =>
    readInitialExpandedGroups(activeGroupId),
  );

  useEffect(() => {
    if (!activeGroupId) return;
    setExpandedGroupIds((current) => {
      if (current.has(activeGroupId)) return current;
      const next = new Set(current);
      next.add(activeGroupId);
      return next;
    });
  }, [activeGroupId]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        EXPANDED_GROUPS_STORAGE_KEY,
        JSON.stringify([...expandedGroupIds]),
      );
    } catch {
      /* non-fatal */
    }
  }, [expandedGroupIds]);

  const toggleGroup = (groupId: AdminNavGroupId) => {
    setExpandedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const renderNavItem = (item: AdminNavItem) => {
    const active =
      location.pathname === item.route || location.pathname.startsWith(`${item.route}/`);
    const Icon = item.icon;
    const button = (
      <ListItemButton
        key={item.id}
        component={NavLink}
        to={item.route}
        selected={active}
        sx={{
          borderRadius: 1.5,
          mb: 0.25,
          py: 0.5,
          minHeight: 0,
          justifyContent: drawerOpen ? "flex-start" : "center",
          px: drawerOpen ? 2 : 1.5,
        }}
      >
        <ListItemIcon sx={{ minWidth: drawerOpen ? 36 : 0, justifyContent: "center" }}>
          <Icon fontSize="small" />
        </ListItemIcon>
        {drawerOpen ? (
          <ListItemText
            primary={item.label}
            slotProps={{ primary: { noWrap: true, variant: "body2" } }}
          />
        ) : null}
        {drawerOpen && !item.built ? (
          <Chip
            label="soon"
            size="small"
            variant="outlined"
            sx={{ ml: "auto", height: 20 }}
          />
        ) : null}
      </ListItemButton>
    );
    return drawerOpen ? (
      button
    ) : (
      <Tooltip key={item.id} title={item.label} placement="right">
        {button}
      </Tooltip>
    );
  };

  return (
    <Box sx={{ overflowX: "hidden" }}>
      {drawerOpen && deployment.showSwitcher ? (
        <Box sx={{ display: { xs: "block", md: "none" }, px: 1.5, pt: 1, pb: 0.5 }}>
          <ProviderSwitcher fullWidth />
        </Box>
      ) : null}
      <List sx={{ px: 1 }}>
        {drawerOpen
          ? visibleGroups.map((group) => {
              const expanded = expandedGroupIds.has(group.id);
              const itemsId = `admin-nav-group-${group.id}`;
              return (
                <Box component="li" key={group.id} sx={{ display: "block" }}>
                  <ListItemButton
                    onClick={() => toggleGroup(group.id)}
                    aria-expanded={expanded}
                    aria-controls={itemsId}
                    sx={{ borderRadius: 1.5, px: 2, py: 0.75 }}
                  >
                    <ListItemText
                      primary={group.label}
                      slotProps={{ primary: { variant: "subtitle2", noWrap: true } }}
                    />
                    {expanded ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </ListItemButton>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List id={itemsId} disablePadding>
                      {group.items.map(renderNavItem)}
                    </List>
                  </Collapse>
                </Box>
              );
            })
          : visibleItems.map(renderNavItem)}
      </List>
    </Box>
  );
}
